import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import { useApolloClient } from '@apollo/react-hooks'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import MediaQuery from 'react-responsive'
import { dataIdFromObject } from 'graphql/apolloClient'
import { parse } from 'query-string'
import { useKey } from 'react-use'

import ImageFilePrefetcher from 'components/ImageFilePrefetcher'
import PrototypeLoader from 'components/Loader/PrototypeLoader'
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import FlowView from './FlowView'
import { tone, useQueryRetry } from 'utils'
import MarkStartOfPrototype from './components/MarkStartOfPrototype'
import Button, { LinkButton } from 'components/Button'
import KeyHandler, { KEYUP } from 'react-key-handler'
import LongPress from 'components/LongPress'
import SupportsTouch from 'components/SupportsTouch'
import OpenModalViaProp from 'containers/ModalManager/OpenModalViaProp'
import ClosePrototypeAlert, {
  localStorageKey,
} from 'containers/modals/ClosePrototypeAlert'
import ErrorMessage from 'components/ErrorMessage'
import {
  VersionLink,
  useVersioning,
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
} from 'modules/versioning'
import { ErrorHandler } from 'modules/tracing'
import { ReactComponent as ImageSlash } from 'icons/image-slash-64.svg'
import Banner from 'containers/Banner'
import { KeyboardShortcutsModal } from 'containers/modals/KeyboardShortcutsModal'
import { useModalContext } from 'containers/ModalManager'
import { keyWithModifier } from 'utils/keyboardEvents'

import {
  GetRelatedArtboardsDocument,
  PrototypePropsFragmentDoc,
  useGetArtboardsForPrototypeQuery,
} from 'gqlTypes'

const ARTBOARDS_LIST_LIMIT = 40

// Styles
const Absolute = styled.div`
  position: absolute;
  top: ${props => props.top || 'auto'};
  right: ${props => props.right || 'auto'};
  bottom: ${props => props.bottom || 'auto'};
  left: ${props => props.left || 'auto'};
  ${props =>
    props.zIndex &&
    css`
      z-index: ${props.zIndex};
    `};
`

const prototypeButtonStyle = css`
  color: ${({ theme }) => theme.colors.text.light.base};
  background-color: ${({ theme }) => tone(4, theme.colors.tungsten)};

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colors.text.light.base};
    background-color: ${({ theme }) => tone(5, theme.colors.tungsten)};
  }

  &:active {
    color: ${({ theme }) => theme.colors.text.light.base};
    background-color: ${({ theme }) => tone(3, theme.colors.tungsten)};
  }
`

const CloseButton = styled(LinkButton)`
  ${prototypeButtonStyle};
`

const ViewArtboardPrototypeButtonBase = props => (
  <Button as={VersionLink} {...props} />
)

const ViewArtboardPrototypeButton = styled(ViewArtboardPrototypeButtonBase)`
  ${prototypeButtonStyle};
`

const StyledPrototypeLoader = styled(PrototypeLoader)`
  width: 140px;
  height: 140px;
`

/**
 * TODO: reuse code between Layout and LoadingPage
 * https://github.com/sketch-hq/Cloud/issues/3543
 *
 * Also, if you are changing Container or LoadingStateContainer
 * change LoadingPage as well
 */
const LoadingStateContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
`

const Container = styled.div`
  display: flex;
  flex: 1 0 auto;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.background.dark.base};
`

// Hooks
const useClosePrototypePath = () => {
  const location = useLocation()
  const { shareID, permanentArtboardShortId } = useParams()

  const { getPathname } = useVersioning()

  if (location?.state?.fromOverview || false) {
    return getPathname({
      routeKey: 'SHARE_VIEW',
      routeParams: {
        shareID,
      },
    })
  } else {
    return getPathname({
      routeKey: 'ARTBOARD_DETAIL',
      routeParams: {
        shareID,
        permanentArtboardShortId,
      },
    })
  }
}

// Helpers
const handleFetchMore = async ({
  fetchMore,
  after,
  permanentArtboardShortId,
  documentVersionShortId,
  shareIdentifier,
  onError,
}) => {
  try {
    return await fetchMore({
      query: GetRelatedArtboardsDocument,
      variables: {
        after,
        permanentArtboardShortId,
        documentVersionShortId,
        shareIdentifier,
        limit: ARTBOARDS_LIST_LIMIT,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const previousEntries =
          previousResult?.artboard?.relatedArtboards?.entries || []
        const newEntries =
          fetchMoreResult?.artboard?.relatedArtboards?.entries || []

        const allEntries = [...previousEntries, ...newEntries]

        const result = Object.assign({}, fetchMoreResult)
        result.artboard.relatedArtboards.entries = allEntries

        return result
      },
    })
  } catch (err) {
    onError(err)
  }
}

function getArtboardFromCache(
  client,
  permanentArtboardShortId,
  documentVersionShortId
) {
  try {
    return client.readFragment({
      id: dataIdFromObject({
        __typename: 'Artboard',
        permanentArtboardShortId,
        documentVersionShortId,
      }),
      fragment: PrototypePropsFragmentDoc,
    })
  } catch (error) {
    ErrorHandler.ignore(
      error,
      'Could not get artboard from cache. Attempting to get artboard from other source.'
    )
  }
}

const getShowHotzones = query => {
  const { hotspots } = parse(query)
  if (!hotspots) {
    return true
  }
  return !(hotspots.toLowerCase() === 'false' || hotspots === '0')
}

const hasSeenClosePrototypeAlert = () => {
  const item = window.localStorage.getItem(localStorageKey)

  return !item || item === 'false'
}

// Components
const ClosePrototypeButton = ({ closePath }) => {
  return (
    <CloseButton small to={closePath}>
      Close
    </CloseButton>
  )
}

ClosePrototypeButton.propTypes = {
  closePath: PropTypes.string.isRequired,
}

const ViewArtboardButton = () => {
  const { shareID, permanentArtboardShortId } = useParams()

  return (
    <ViewArtboardPrototypeButton
      to={{
        routeKey: 'ARTBOARD_DETAIL',
        routeParams: {
          shareID,
          permanentArtboardShortId,
        },
      }}
      small>
      View Artboard
    </ViewArtboardPrototypeButton>
  )
}

const ClosePrototypeHandler = ({ closePath, children }) => {
  const history = useHistory()

  const closePrototype = () => {
    history.push(closePath)
  }

  return (
    <>
      <KeyHandler
        keyEventName={KEYUP}
        keyValue="Escape"
        onKeyHandle={closePrototype}
      />
      <LongPress onLongPress={closePrototype}>{children}</LongPress>
    </>
  )
}

ClosePrototypeHandler.propTypes = {
  closePath: PropTypes.string.isRequired,
  children: PropTypes.any,
}

const PrototypeView = () => {
  const location = useLocation()
  const { permanentArtboardShortId } = useParams()

  const client = useApolloClient()
  const { versionShortId, share } = useVersioning()
  const [fetchMoreError, setFetchMoreError] = useState()
  const closePath = useClosePrototypePath()
  const showHotzones = getShowHotzones(location.search)

  const { isModalOpen, showModal, hideModal } = useModalContext()

  const [firstArtboardShortId] = useState(permanentArtboardShortId)

  const [loaderFinished, setLoaderFinished] = useState(false)
  const onLoaderFinished = () => {
    setLoaderFinished(true)
  }

  const [showClosePrototypeAlert, setShowClosePrototypeAlert] = useState(
    hasSeenClosePrototypeAlert()
  )
  const onClickClosePrototypeAlert = () => {
    window.localStorage.setItem(localStorageKey, true)
    setShowClosePrototypeAlert(false)
  }

  // Keyboard shortcut (⌘ + /) to open keyboard shortcuts modal
  useKey(keyWithModifier('/'), () => {
    if (isModalOpen) {
      hideModal()
    } else {
      showModal(KeyboardShortcutsModal)
    }
  })

  const { data, loading, fetchMore, error, retryState } = useQueryRetry(
    useGetArtboardsForPrototypeQuery({
      variables: {
        permanentArtboardShortId: firstArtboardShortId,
        documentVersionShortId: versionShortId,
        shareIdentifier: share.identifier,
        limit: ARTBOARDS_LIST_LIMIT,
      },
    }),
    {
      retryIf: ({ data }) => {
        if (!data || !data.artboard) {
          return false
        }

        const files = [
          data.artboard.files,
          data.artboard.relatedArtboards.entries.map(x => x.files).flat(),
        ].flat()

        return files.some(file => !file.url)
      },
    }
  )

  const renderPrototype = () => {
    if (
      isUpgradeToLatestNeeded(error) ||
      isUpgradeToLatestNeeded(fetchMoreError)
    ) {
      return <UpgradeToLatestVersion error={error} />
    }

    if (retryState === 'retrying') {
      return <DynamicLoadingPage />
    }

    const firstArtboard = data?.artboard
    const after = data?.artboard?.relatedArtboards?.meta?.after
    const entries = [
      firstArtboard,
      ...(data?.artboard?.relatedArtboards?.entries || []),
    ].filter(artboard => !!artboard)

    const shouldLoadMoreArtboards = !!after

    const imageFiles = entries
      .map(artboard =>
        artboard.files.map(file => ({
          ...file,
          artboard: artboard.identifier,
        }))
      )
      .flat()

    const artboard =
      getArtboardFromCache(client, permanentArtboardShortId, versionShortId) ||
      (entries ? firstArtboard : null)

    if (loading || shouldLoadMoreArtboards) {
      handleFetchMore({
        fetchMore,
        permanentArtboardShortId,
        documentVersionShortId: versionShortId,
        shareIdentifier: share.identifier,
        after,
        onError: err => setFetchMoreError(err),
      })

      return <DynamicLoadingPage />
    }

    if (!loading && !artboard) {
      return (
        <ErrorMessage
          dark
          style={{ position: 'absolute' }}
          icon={<ImageSlash />}
          iconSize="medium"
          title="Artboard not found"
        />
      )
    }

    return (
      <ImageFilePrefetcher files={imageFiles}>
        {progress => {
          if (!loaderFinished) {
            return (
              <LoadingStateContainer>
                <StyledPrototypeLoader
                  percentage={progress * 100}
                  onFinished={onLoaderFinished}
                />
              </LoadingStateContainer>
            )
          }

          return (
            <FlowView artboard={artboard} shouldShowHotzones={showHotzones} />
          )
        }}
      </ImageFilePrefetcher>
    )
  }

  return (
    <>
      <Banner />
      <ClosePrototypeHandler closePath={closePath}>
        <Container>
          <MarkStartOfPrototype />

          {renderPrototype()}

          <MediaQuery maxWidth={1024}>
            {matches => (
              <SupportsTouch>
                {hasTouch =>
                  matches && hasTouch ? (
                    <OpenModalViaProp
                      isOpen={showClosePrototypeAlert}
                      modal={ClosePrototypeAlert}
                      modalProps={{
                        onCancel: onClickClosePrototypeAlert,
                      }}
                    />
                  ) : (
                    <div>
                      <Absolute bottom="0.5rem" left="0.5rem" zIndex={999}>
                        <ClosePrototypeButton closePath={closePath} />
                      </Absolute>
                      <Absolute bottom="0.5rem" right="0.5rem" zIndex={999}>
                        <ViewArtboardButton />
                      </Absolute>
                    </div>
                  )
                }
              </SupportsTouch>
            )}
          </MediaQuery>
        </Container>
      </ClosePrototypeHandler>
    </>
  )
}

export default PrototypeView
